import each from 'lodash/each';
import every from 'lodash/every';
import find from 'lodash/find';
import forIn from 'lodash/forIn';
import get from 'lodash/get';
import includes from 'lodash/includes';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import reject from 'lodash/reject';
import some from 'lodash/some';
import times from 'lodash/times';
import values from 'lodash/values';

import { generateEid } from 'zoomforth_codesbane_editor/lib/id_generators.js';

import {
  doesTileOverlapAnyTile,
  getNextUnboundedAvailablePosition
} from 'zoomforth_codesbane_editor/lib/grid_positions.js';

import predefinedBlocksContainers
  from 'zoomforth_zf/predefined_blocks/predefined_blocks_containers.js';

import {
  getBlockById,
  transformBlockDataToLegacyData
} from 'zoomforth_zf/predefined_blocks/blocks_transformers.js';

/**

  Extract standard gridPositions data and editor only override data.

  The inverse of `applyGridPositionsOverride`

  Accepts `overriddenGridPositions` which is an array of standard gridPositions
  with `eid` values included. 

  Returns a 2-length array of 
  - standard gridPositions
  - the part of the sectionOverrides data which is derrived from gridPosition data

*/
export const extractGridPositionsOverride = (overriddenGridPositions)=> {

  const pageEntityGridPositions = [];
  const eidMask = [];
  const editorOnlyGridPositionsByEid = {};

  each(overriddenGridPositions, ({ uuid, eid, sizex, sizey, col, row })=> {

    if (uuid) {
      pageEntityGridPositions.push({
        uuid, sizex, sizey, col, row
      });

    } else {
      editorOnlyGridPositionsByEid[`${eid}`] = {
        eid, sizex, sizey, col, row
      };
    }

    eidMask.push(eid);

  });

  return [
    pageEntityGridPositions,
    {
      eidMask,
      editorOnlyGridPositionsByEid
    }
  ];
};


/**

  Merge standard `gridPositions` data with `sectionOverrides` data.

  The inverse of `extractGridPositionsOverride`

  Return a new section object with the various overrides applied to it.
*/
export const applyGridPositionsOverride = (gridPositions, sectionOverrides)=> {

  const { eidMask, editorOnlyGridPositionsByEid } = sectionOverrides;

  const gridPositionsCopy = [ ...gridPositions ];

  const overriddenGridPositions = map(eidMask, (eid)=> {

    if (editorOnlyGridPositionsByEid[`${eid}`]) {
      return editorOnlyGridPositionsByEid[`${eid}`];
    }
    return {
      eid,
      ...gridPositionsCopy.shift()
    };
  });
  return overriddenGridPositions;
};


/**

  Given `sectionOverrides` data and an overriddenGridPosition yield an editor only "tile".

  Editor tile "generator" corresponding to standard `generateTileFromGridPosition` method.

*/
export const generateEditorTileFromGridPosition = (sectionOverrides, { eid, row, col, sizex, sizey })=>
  ({
    eid,
    row,
    col,
    sizex,
    sizey,
    ...get(sectionOverrides, `editorOnlyTileDataByEid.${eid}`, {})
  });


/**

  Given an editor only tile strip out the non-"grid position" parts.

  Editor tile "degenerator" corresponding to standard `degenerateTileToGridPosition` method.
*/
export const degenerateEditorTileToGridPosition = ({
  eid, row, col, sizex, sizey
})=>
  ({ eid, row, col, sizex, sizey });

  
/**

  Return a new section and new sectionOverrides but apply `newTileData` to the given tile
  with the given `eid`.
*/
export const setEditorTileData = (section, sectionOverrides, eidToUpdate, newTileData)=> {
  const { eidMask, editorOnlyGridPositionsByEid, editorOnlyTileDataByEid } = sectionOverrides;

  const newSectionOverrides = {
    eidMask,
    editorOnlyGridPositionsByEid,
    editorOnlyTileDataByEid: {
      ...editorOnlyTileDataByEid,
      [eidToUpdate]: newTileData
    }
  };
  return [
    section,
    newSectionOverrides
  ];
};

export const removeEditorTileData = (section, sectionOverrides, eidToRemove)=> {
  const newSectionOverrides = {
    ...sectionOverrides,
    editorOnlyTileDataByEid: (
      omitBy(sectionOverrides.editorOnlyTileDataByEid,
        (tile, eid) => eidToRemove === eid)
    )
  };

  return [
    section,
    newSectionOverrides
  ];
};

/**

  Return a new section and new sectionOverrides but add a `uuid` to to the tile with 
  the given `eid`

*/
export const setEditorTileContent = (section, sectionOverrides, eidToSet, uuid)=> {

  const { gridPositions, ...restSection } = section;
  const overriddenGridPositions = applyGridPositionsOverride(gridPositions, sectionOverrides);

  const newOverriddenGridPositions = map(overriddenGridPositions,
    ({ eid, ...rest })=>
      eidToSet === eid ?
        ({
          ...rest,
          eid,
          uuid,
          isTileCreator: false
        })
      :
        ({
          ...rest,
          eid
        })
  );

  const [newGridPositions, { eidMask, editorOnlyGridPositionsByEid }] = extractGridPositionsOverride(newOverriddenGridPositions);
  const newSection = {
    ...restSection,
    gridPositions: newGridPositions
  };

  const newSectionOverrides = {
    ...sectionOverrides,
    eidMask,
    editorOnlyGridPositionsByEid
  };

  return [
    newSection,
    newSectionOverrides
  ];

};


/**
  Return a new section and new sectionOverrides after adding a new tile with the given `eid`
*/
export const createEditorTileAtEnd = (section, sectionOverrides, newEid, { sizex=1, sizey=1 }={})=> {
  
  const { gridPositions, columnCount, ...rest } = section;

  const overriddenGridPositions = applyGridPositionsOverride(gridPositions, sectionOverrides);
  const newGridPos = {
    eid:newEid,
    ...getNextUnboundedAvailablePosition({ sizex, sizey, gridPositions:overriddenGridPositions, columnCount })
  };

  const [newGridPositions, { eidMask, editorOnlyGridPositionsByEid }] = 
    extractGridPositionsOverride([ ...overriddenGridPositions, newGridPos ]);

  const newSection = { 
    gridPositions:newGridPositions, 
    columnCount, 
    ...rest 
  };

  const { editorOnlyTileDataByEid } = sectionOverrides;

  const newSectionOverrides = {
    eidMask: eidMask,
    editorOnlyGridPositionsByEid,
    editorOnlyTileDataByEid: {
      ...editorOnlyTileDataByEid,
      [newEid]: {
        isTileCreator: true
      }
    }
  };

  return [
    newSection,
    newSectionOverrides
  ];
};

/**
  Return a new section and new sectionOverrides after adding a new tile with
  the given `eid` to a given position in a grid.

  Warning: It is the callers responsibility to ensure there are no collisions in
  the input data. No calculations / consideration is given to collisions here.
  i.e., if sizex and col would yield to an overflow over another tile or
  outside of the grid then an error or some undefined behavior will occur.
*/
export const createEditorTileAtPosition = (
  section,
  sectionOverrides,
  newEid,
  {
    sizex,
    sizey,
    col,
    row
  }={},
  {
    ...extraTileCreatorData
  }={}
)=> {

  const { gridPositions, columnCount, ...rest } = section;

  const overriddenGridPositions = applyGridPositionsOverride(gridPositions, sectionOverrides);
  const newGridTilePos = {
    eid: newEid,
    row,
    col,
    sizex,
    sizey
  };

  const [newGridPositions, { eidMask, editorOnlyGridPositionsByEid }] = 
    extractGridPositionsOverride([ ...overriddenGridPositions, newGridTilePos ]);

  const newSection = {
    ...rest,
    gridPositions: newGridPositions,
    columnCount
  };

  const newSectionOverrides = {
    eidMask: eidMask,
    editorOnlyGridPositionsByEid,
    editorOnlyTileDataByEid: {
      ...sectionOverrides.editorOnlyTileDataByEid,
      [newEid]: {
        ...extraTileCreatorData,
        isTileCreator: true
      }
    }
  };

  return [
    newSection,
    newSectionOverrides
  ];
};

/**
  Return a new section and new sectionOverrides after removing a tile with the given `eid`
*/
export const removeEditorTiles = (section, sectionOverrides, eidsToRemove)=> {
  const { gridPositions, ...restSection } = section;
  const overriddenGridPositions = applyGridPositionsOverride(gridPositions, sectionOverrides);

  const newOverriddenGridPositions = reject(overriddenGridPositions, ({ eid })=> includes(eidsToRemove, eid));

  const [newGridPositions, { eidMask, editorOnlyGridPositionsByEid }] = 
    extractGridPositionsOverride(newOverriddenGridPositions);

  const newSection = {
    ...restSection,
    gridPositions: newGridPositions
  };
  const newSectionOverrides = {
    eidMask,
    editorOnlyGridPositionsByEid,
    editorOnlyTileDataByEid: omitBy(sectionOverrides.editorOnlyTileDataByEid, (tileData, eid)=> includes(eidsToRemove, eid))
  };

  return [
    newSection,
    newSectionOverrides
  ];
};


/**
  Return the `eid` associated with a particular content `uuid`
*/
export const getEditorTileEidByContentUuid = (section, sectionOverrides, contentUuid)=> {
  const overriddenGridPositions = applyGridPositionsOverride(section.gridPositions, sectionOverrides);
  const match = find(overriddenGridPositions, ['uuid', contentUuid]);
  return match ? match.eid : null;
};

export const getEditorTileGridPositionByEid = (section, sectionOverrides, eid)=> {
  const overriddenGridPositions = applyGridPositionsOverride(section.gridPositions, sectionOverrides);
  return find(overriddenGridPositions, ['eid', eid]);
};

export const getTileCreatorEid = (section, sectionOverrides)=> {
  var foundEid;
  each(sectionOverrides.editorOnlyTileDataByEid, 
    ({ isTileCreator }, eid)=> {
      if (isTileCreator) {
        foundEid = eid;
      }
    }
  );
  return foundEid;
};

export const isTileCreatorTile = (section, sectionOverrides, eid)=>
  !!(sectionOverrides.editorOnlyTileDataByEid[`${eid}`] && 
      sectionOverrides.editorOnlyTileDataByEid[`${eid}`].isTileCreator);

export const hasAnyTileCreator = (editorOnlyTileDataByEid) => (
  some(editorOnlyTileDataByEid,
    (tileData) => tileData.isTileCreator
  )
);

const generateSectionLayoutOverridesMask = (blockSubtype) => {
  /**
   * This function is to consume a section/container subtype and
   * generate an editor mask.
   * 
   * We do this because we want to provide the User with the same
   * tile creators they had in the last editing section.
   * 
   * We *can* do this because we maintain the subtype in the
   * section body that we store in our `v2data` config object in
   * the SQL database.
   * */

  const predefinedSectionLayout = find(predefinedBlocksContainers,
    (blockContainer) => (
      blockContainer.blockSubtype === blockSubtype
    )
  );

  var sectionLayoutOverridesMask = {
    eidMask: [],
    editorOnlyGridPositionsByEid: {},
    editorOnlyTileDataByEid: {}
  };

  if (predefinedSectionLayout) {
    const { childIds } = predefinedSectionLayout;

    const sectionLayoutChildren = map(childIds,
      (childId) => {
        const childBlock = getBlockById(childId);
        return childBlock;
      }
    );

    sectionLayoutOverridesMask = reduce(sectionLayoutChildren,
      (sectionOverrides, childData) => {
        const transformedChild = transformBlockDataToLegacyData(childData);

        const {
          sizex,
          sizey,
          row,
          col,
          isTileCreator
        } = transformedChild;

        if (isTileCreator) {
          const eid = generateEid();

          sectionOverrides.eidMask.push(eid);
          sectionOverrides.editorOnlyGridPositionsByEid[`${eid}`] = {
            row, col, sizex, sizey, eid
          };
          sectionOverrides.editorOnlyTileDataByEid[`${eid}`] = transformedChild;
        }

        return sectionOverrides;

      }, {
        eidMask: [],
        editorOnlyGridPositionsByEid: {},
        editorOnlyTileDataByEid: {}
      }
    );
  }

  return sectionLayoutOverridesMask;
};

/**
  Return the `eid` associated with a particular content `uuid`
*/
export const buildInitialSectionOverrides = (
  section,
  exisitngSectionOverrides={},
  {
    addTileCreator=true,
    shouldMapGridPositionsToEidMask=true,
    shouldMaskOverridesWithPredefinedSection=true
  }={}
) => {
  const {
    blockSubtype,
    columnCount,
    gridPositions
  } = section;

  const sectionLayoutOverridesMask = generateSectionLayoutOverridesMask(blockSubtype);

  const exisitngEditorOnlyGridPositionsByEid =
    get(exisitngSectionOverrides, 'editorOnlyGridPositionsByEid', {});

  const existingEditorOnlyTileDataByEid =
    get(exisitngSectionOverrides, 'editorOnlyTileDataByEid', {});

  const eidMask = [];
  const editorOnlyGridPositionsByEid = {};
  const editorOnlyTileDataByEid = {};

  forIn(exisitngEditorOnlyGridPositionsByEid,
    (existingTileData, existingEid) => {
      const eid = generateEid();
      eidMask.push(eid);

      editorOnlyGridPositionsByEid[`${eid}`] = {
        ...existingTileData,
        eid
      };

      editorOnlyTileDataByEid[`${eid}`] =
        existingEditorOnlyTileDataByEid[`${existingEid}`];
    }
  );

  let shouldAddTileCreator = (
       addTileCreator
    && every(values(editorOnlyTileDataByEid), { 'isTileCreator': false })
  );

  if (shouldMapGridPositionsToEidMask && !isNil(gridPositions)) {
    times(gridPositions.length, () => {
      const eid = generateEid();
      eidMask.push(eid);
    });
  }

  const canInsertSectionOverridesMaskTile = (sectionLayoutMaskGridPosition) => {
    const sectionLayoutMaskGrid__Position_Size__Data =
      pick(sectionLayoutMaskGridPosition, ['row', 'col', 'sizex', 'sizey']);

    return (
      /**
       * If our mask tile creator overlaps a tile
       * with content then we cannot add a tile
       * creator there.
       * */
      !doesTileOverlapAnyTile({
        ...sectionLayoutMaskGrid__Position_Size__Data,
        gridPositions
      })
    );
  };

  /**
   * flag option &&
   * 
   * if we already have existingSectionOverrides then we will just consume that data
   * instead of generating based on the mask.
   * */
  if (shouldMaskOverridesWithPredefinedSection) {
    /**
     * What we are saying here is this:
     * 
     * If we do not have a tile item at a particular position in our section data,
     * but we have a tile creator item in our predefined section layout,
     * then we want to "fill that position", so to speak, with a tile creator
     * because we are expecting the section of this type to have a tile creator there
     * when editing that section!
     * */

    forIn(sectionLayoutOverridesMask.editorOnlyGridPositionsByEid,
      (sectionLayoutMaskGridPosition, eid) => {

        const sectionLayoutTileData = sectionLayoutOverridesMask.editorOnlyTileDataByEid[`${eid}`];

        if (canInsertSectionOverridesMaskTile(sectionLayoutMaskGridPosition)) {
          eidMask.push(eid);
          editorOnlyGridPositionsByEid[`${eid}`] = sectionLayoutMaskGridPosition;
          editorOnlyTileDataByEid[`${eid}`] = sectionLayoutTileData;

          if (sectionLayoutTileData.isTileCreator === true) {
            shouldAddTileCreator = false;
          }
        }
      }
    );
  }

  // We always need at least
  // one tile creator per grid.
  if (shouldAddTileCreator) {
    const eid = generateEid();

    eidMask.push(eid);
    editorOnlyGridPositionsByEid[`${eid}`] = {
      ...getNextUnboundedAvailablePosition({
        sizex: 1,
        sizey: 1,
        gridPositions,
        columnCount
      }),
      eid
    };
    editorOnlyTileDataByEid[`${eid}`] = {
      isTileCreator: true
    };
  }

  const sectionOverrides = {
    eidMask,
    editorOnlyGridPositionsByEid,
    editorOnlyTileDataByEid
  };

  return sectionOverrides;
};